import { useEffect, useRef } from 'react';
import { useSignalR } from '../providers/SignalR';
import { reportError, debug } from '../services/logger';

/** @type {Record<string, string>} */
const ENV_MAP = {
  development: 'dev',
  staging: 'staging',
  production: 'production',
  waystone: 'waystone',
  demo: 'demo',
  custom_production: 'custom-report'
};

/* Every environment shares one hub, so each client sees the others' traffic and filters it out.
   A tag on this list is someone else's message; anything else is a tag this build cannot place. */
const KNOWN_ENVS = new Set([...Object.keys(ENV_MAP), ...Object.values(ENV_MAP)]);

/**
 * Hook for listening to SignalR messages with automatic environment filtering.
 * Messages from different environments (dev, staging, production, demo, custom-report)
 * are automatically filtered out.
 * @param {string} method
 * @param {(...args: unknown[]) => void} callback
 * @returns {void}
 */
export const useSignalRListener = (method, callback) => {
  const { service, isConnected } = useSignalR();
  const callBackRef = useRef(callback);

  useEffect(() => {
    callBackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (isConnected && service) {
      const handler = (...args) => {
        const data = args[0];

        const currentEnv = import.meta.env.VITE_APP_ENV;
        const backendEnv = data?.environment;

        if (!backendEnv) {
          debug(`signalr.${method}`, 'no environment in payload - processing unfiltered', {
            currentEnv
          });
          callBackRef.current(...args);
          return;
        }

        const mappedBackendEnv = ENV_MAP[backendEnv] || backendEnv;

        if (mappedBackendEnv === currentEnv) {
          debug(`signalr.${method}`, 'environment match - processing', { backendEnv, currentEnv });
          callBackRef.current(...args);
        } else if (KNOWN_ENVS.has(backendEnv)) {
          debug(`signalr.${method}`, 'other environment - ignoring', { backendEnv, currentEnv });
        } else {
          /* Loud on purpose, and in every build. A tag this build cannot place never matches, so
             every message carrying it is dropped, and the table it feeds just stops updating with
             nothing anywhere saying why. That silence is what left extraction rows sitting at
             their old status until someone reloaded the page. It means ENV_MAP has drifted from
             what the backend sends. */
          reportError(
            'signalr.unknownEnvironment',
            new Error('SignalR message from unknown environment ignored'),
            {
              method,
              backendEnv,
              mappedBackendEnv,
              currentEnv
            }
          );
        }
      };

      service.on(method, handler);

      return () => {
        service.off(method, handler);
      };
    }
  }, [isConnected, service, method]);
};
